/** One Source: metadata and full text (child route of the app shell) */

import { Link, useParams } from 'react-router-dom'
import { Archive, ArchiveRestore, ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'
import { useSetSourceArchived, useSource } from '../hooks/use-project-content'
import { useAppStore } from '../stores/app-store'
import { projectPath } from '../components/shell/nav-config'
import { useT } from '../i18n'
import { formatBytes } from '../lib/format-bytes'

export function ProjectSourceDetailPage() {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const { projectSlug = 'default', sourceId } = useParams<{ projectSlug: string; sourceId: string }>()
  const { data: source, isLoading, error } = useSource(projectSlug, sourceId)
  const setArchived = useSetSourceArchived(projectSlug)

  const toggleArchived = (archived: boolean) => {
    if (!source) return
    setArchived.mutate({ id: source.id, archived }, {
      onSuccess: () => toast.success(t(archived ? 'source.archived' : 'source.restored')),
      onError: (err) => toast.error(t('source.archiveFailed', { message: err instanceof Error ? err.message : String(err) })),
    })
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-5 px-4 py-6 md:px-8 md:py-10">
      <Link to={projectPath(projectSlug, 'sources')} className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('source.back')}
      </Link>
      {isLoading ? (
        <p className="text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : error || !source ? (
        <p role="alert" className="text-sm text-danger">{t('source.notFound')}</p>
      ) : (
        <>
          <header className="space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <h1 className="min-w-0 break-words font-display text-2xl font-semibold tracking-tight text-fg">{source.title}</h1>
              {source.status !== 'superseded' && (
                <button
                  type="button"
                  onClick={() => toggleArchived(source.status === 'active')}
                  disabled={setArchived.isPending}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-fg-muted hover:bg-surface-hover hover:text-fg disabled:opacity-50"
                >
                  {source.status === 'active'
                    ? <Archive className="h-4 w-4" aria-hidden="true" />
                    : <ArchiveRestore className="h-4 w-4" aria-hidden="true" />}
                  {t(source.status === 'active' ? 'source.archive' : 'source.restore')}
                </button>
              )}
            </div>
            <p className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-fg-subtle">
              <span className="break-all">{source.filename}</span>
              <span>{source.origin}{source.originRef ? ` · ${source.originRef}` : ''}</span>
              <span>{formatBytes(source.sizeBytes, locale)}</span>
              <span>{new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(source.createdAt))}</span>
            </p>
            {source.status === 'superseded' && (
              <p className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-sm text-fg-muted">
                {t('source.replacedBy')}
                {source.supersededBy && (
                  <>
                    {' · '}
                    <Link to={projectPath(projectSlug, `sources/${source.supersededBy}`)} className="font-medium text-accent-text hover:underline">
                      {t('source.openNewer')}
                    </Link>
                  </>
                )}
              </p>
            )}
            {source.status === 'archived' && (
              <p className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-sm text-fg-muted">{t('source.archivedNote')}</p>
            )}
          </header>
          <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-xl border border-border bg-surface-muted p-4 text-sm leading-relaxed text-fg">
            {source.content}
          </pre>
        </>
      )}
    </div>
  )
}
